/**
 * Стандартные индикаторы, рассчитываемые в браузере по видимым барам агрегата.
 * Все они эквивариантны к линейному преобразованию цены, поэтому одинаково корректны
 * и в нормированных единицах (этап 1), и в пунктах. Интерпретационных подсказок нет.
 */
import type { Bar } from '@mt/protocol';

export interface Point {
  t: number;
  v: number;
}

export function sma(bars: Bar[], period: number): Point[] {
  const out: Point[] = [];
  let sum = 0;
  for (let i = 0; i < bars.length; i++) {
    sum += bars[i]!.c;
    if (i >= period) sum -= bars[i - period]!.c;
    if (i >= period - 1) out.push({ t: bars[i]!.t, v: sum / period });
  }
  return out;
}

export function ema(bars: Bar[], period: number): Point[] {
  const out: Point[] = [];
  const k = 2 / (period + 1);
  let e: number | null = null;
  for (let i = 0; i < bars.length; i++) {
    const c = bars[i]!.c;
    if (i < period - 1) continue;
    if (e === null) {
      let s = 0;
      for (let j = i - period + 1; j <= i; j++) s += bars[j]!.c;
      e = s / period;
    } else e = c * k + e * (1 - k);
    out.push({ t: bars[i]!.t, v: e });
  }
  return out;
}

/**
 * VWAP-аналог агрегата: средняя типичная цена (H+L+C)/3, взвешенная по относительной
 * активности v (определение активности — в правилах), накопленная с начала окна истории.
 */
export function vwap(bars: Bar[]): Point[] {
  const out: Point[] = [];
  let pv = 0;
  let vv = 0;
  for (const b of bars) {
    const w = Math.max(b.v, 0);
    pv += ((b.h + b.l + b.c) / 3) * w;
    vv += w;
    if (vv > 0) out.push({ t: b.t, v: pv / vv });
  }
  return out;
}

export function bollinger(bars: Bar[], period: number, mult: number): { upper: Point[]; lower: Point[]; mid: Point[] } {
  const upper: Point[] = [];
  const lower: Point[] = [];
  const mid: Point[] = [];
  for (let i = period - 1; i < bars.length; i++) {
    let s = 0;
    for (let j = i - period + 1; j <= i; j++) s += bars[j]!.c;
    const m = s / period;
    let q = 0;
    for (let j = i - period + 1; j <= i; j++) q += (bars[j]!.c - m) ** 2;
    const sd = Math.sqrt(q / period);
    const t = bars[i]!.t;
    mid.push({ t, v: m });
    upper.push({ t, v: m + mult * sd });
    lower.push({ t, v: m - mult * sd });
  }
  return { upper, lower, mid };
}

/** RSI по Уайлдеру. */
export function rsi(bars: Bar[], period: number): Point[] {
  const out: Point[] = [];
  if (bars.length <= period) return out;
  let gain = 0;
  let loss = 0;
  for (let i = 1; i <= period; i++) {
    const d = bars[i]!.c - bars[i - 1]!.c;
    if (d > 0) gain += d;
    else loss -= d;
  }
  gain /= period;
  loss /= period;
  const val = () => (loss === 0 ? 100 : 100 - 100 / (1 + gain / loss));
  out.push({ t: bars[period]!.t, v: val() });
  for (let i = period + 1; i < bars.length; i++) {
    const d = bars[i]!.c - bars[i - 1]!.c;
    gain = (gain * (period - 1) + Math.max(d, 0)) / period;
    loss = (loss * (period - 1) + Math.max(-d, 0)) / period;
    out.push({ t: bars[i]!.t, v: val() });
  }
  return out;
}

/** Средний истинный диапазон — помогает оценить типичный размах. */
export function atr(bars: Bar[], period: number): Point[] {
  const out: Point[] = [];
  let a: number | null = null;
  for (let i = 1; i < bars.length; i++) {
    const b = bars[i]!;
    const pc = bars[i - 1]!.c;
    const tr = Math.max(b.h - b.l, Math.abs(b.h - pc), Math.abs(b.l - pc));
    a = a === null ? tr : (a * (period - 1) + tr) / period;
    if (i >= period) out.push({ t: b.t, v: a });
  }
  return out;
}
